// The free-item offer's strip on a package card (#5747): a picture of the item
// the package includes, beside a FREE caption. ONE component for both card
// sizes -- it used to be pasted into each.
//
// The picture is the child's OWN portrait composited into the product, drawn
// through the gifts storefront's one renderer (`Item previewOnly`, which owns
// the per-variant geometry -- never a second way to draw a product with a pose
// on it, and never an extra wrapper between Item and its square). Which add-on
// and which photo is freeItemThumbnail's decision: the most popular one of the
// set that this child has a usable photo for. With no usable photo the plain
// product photo stands in, as it did before compositing.

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Item, { canRenderPreview } from '../_pages/Holiday/Builder/Item';
import resolveOrderType from '../_pages/Holiday/Builder/orderType';
import usePoses from '../../hooks/usePoses';
import useBackgrounds from '../../hooks/useBackgrounds';
import useImageOrientations from '../../hooks/useImageOrientations';
import { useCustomer } from '../../contexts/CustomerContext';
import { money, freeAddonsOf } from '../../utils/offerPricing';
import { freeItemThumbnail } from './packageCardOffer';
import styles from './PackageDisplayV2.module.sass';

// What the gifts renderer can draw: its own answer (canRenderPreview) AND a
// variant it knows (giftRenderable, the server's GIFT_RENDERABLE_CSS_NAMES) --
// an unknown css_name would land in Item's fallback branch, a Sentry report
// from every pageview and a pose-less photo in the square.
const canComposite = (addon) => !!addon?.giftRenderable && canRenderPreview(addon, addon.cssName);

export default function FreeItemThumb({ offer, packageId, pricelist }) {
  const { customer } = useCustomer();
  const { data: poses, isPlaceholderData: posesPending } = usePoses(customer?.currentStudent?.id, packageId);
  const { data: backgrounds } = useBackgrounds(packageId);
  // Orientation is MEASURED off the image (never read off a crop number), and
  // only when a design in the set refuses landscape -- otherwise the rule never
  // consults it, and every card would be probing every watermark for nothing.
  const needsOrientation = freeAddonsOf(offer).some((a) => a?.allowsLandscapePoses === false);
  const urls = useMemo(
    () => (needsOrientation ? (poses || []).map((p) => p?.watermarkUrl || p?.imageUrl).filter(Boolean) : []),
    [needsOrientation, poses],
  );
  const orientations = useImageOrientations(urls);
  const measured = urls.every((url) => !!orientations[url]);
  // The same resolution the gifts shelf makes, so "most popular" here is what
  // that shelf's Most Popular sort would lead with for this customer.
  const orderType = resolveOrderType({
    pricelistSeason: pricelist?.season,
    schoolTypeId: customer?.currentSchool?.schoolTypeId,
    poses: poses || [],
  });

  // Never decide on placeholder data or unmeasured photos: the picked item and
  // its quoted value would otherwise change under the parent after first paint
  // (the picker's readyToDecide rule).
  if (posesPending || !measured) return null;

  const thumb = freeItemThumbnail(offer, {
    orderType, poses: poses || [], orientations, canComposite,
  });
  if (!thumb) return null;
  const { addon, pose } = thumb;

  return (
    <div className={styles.freeItemThumb}>
      {pose ? (
        // A square, like the shelf tile: the products are different shapes and
        // Item fills whatever box it is given.
        <div className={styles.freeItemThumbPreview} role="img" aria-label={thumb.alt}>
          <Item
            previewOnly
            addon={addon}
            variant={addon.cssName}
            background={addon.backgroundSelection ? (backgrounds || [])[0] : undefined}
            pose={pose}
          />
        </div>
      ) : (
        <img src={thumb.src} alt={thumb.alt} />
      )}
      <span className={styles.freeItemThumbText}>
        <span>FREE with this package</span>
        {thumb.value != null && <small>{`${money(thumb.value)} value`}</small>}
      </span>
    </div>
  );
}

FreeItemThumb.defaultProps = {
  offer: null,
  pricelist: null,
};

FreeItemThumb.propTypes = {
  // The resolved promotional offer (useOfferCoupon); null when none is live.
  // eslint-disable-next-line react/forbid-prop-types
  offer: PropTypes.object,
  packageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  // The pricelist (usePricelist); its season resolves the order type.
  // eslint-disable-next-line react/forbid-prop-types
  pricelist: PropTypes.object,
};
